import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "@/api/client";

/** Prefer the API's own explanation (e.g. "create a viewing slot first") over a generic one. */
function apiDetail(error: unknown, fallback: string): string {
  if (error && typeof error === "object" && "detail" in error) {
    const detail = (error as { detail?: unknown }).detail;
    if (typeof detail === "string") return detail;
  }
  return fallback;
}


export type ApplicationFilters = {
  status?: string;
  kind?: string;
  propertyId?: string;
};

const listKey = (f: ApplicationFilters) =>
  ["applications", f.status ?? "all", f.kind ?? "all", f.propertyId ?? "all"] as const;

export function useApplications(filters: ApplicationFilters = {}) {
  return useQuery({
    queryKey: listKey(filters),
    staleTime: 30_000,
    refetchInterval: 60_000, // keep the inbox reasonably fresh
    queryFn: async () => {
      const query: Record<string, string> = {};
      if (filters.status) query.status = filters.status;
      if (filters.kind) query.kind = filters.kind;
      if (filters.propertyId) query.property_id = filters.propertyId;
      const { data, error } = await apiClient.GET("/api/v1/applications", {
        params: { query },
      });
      if (error || !data) throw new Error("Failed to load applications");
      return data;
    },
  });
}

export function useApplication(id: string | undefined) {
  return useQuery({
    queryKey: ["applications", "detail", id],
    enabled: Boolean(id),
    staleTime: 0,
    refetchInterval: 15_000, // candidate actions (uploads, booking, offer) land live
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/applications/{application_id}",
        { params: { path: { application_id: id as string } } },
      );
      if (error || !data) throw new Error("Failed to load application");
      return data;
    },
  });
}

export function useCloseApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, reason }: { id: string; reason?: string }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/close",
        {
          params: { path: { application_id: id } },
          body: { reason: reason ?? null },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not close the application"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useViewingInvites() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      propertyId: string;
      minScore: number;
      dryRun: boolean;
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/viewing-invites",
        {
          body: {
            property_id: input.propertyId,
            min_score: input.minScore,
            dry_run: input.dryRun,
          },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not process viewing invites"));
      return data;
    },
    onSuccess: (_data, vars) => {
      if (!vars.dryRun) qc.invalidateQueries({ queryKey: ["applications"] });
    },
  });
}

export function useApplicationDocuments(applicationId: string | undefined) {
  return useQuery({
    queryKey: ["applications", "documents", applicationId],
    enabled: Boolean(applicationId),
    staleTime: 0,
    refetchInterval: 15_000, // uploads happen on the candidate's side
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/applications/{application_id}/documents",
        { params: { path: { application_id: applicationId as string } } },
      );
      if (error || !data) throw new Error("Failed to load documents");
      return data;
    },
  });
}

export function useDocumentPresets(incomeType: string | null | undefined) {
  return useQuery({
    queryKey: ["application-document-presets", incomeType ?? "all"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/application-document-presets",
        { params: { query: incomeType ? { income_type: incomeType } : {} } },
      );
      if (error || !data) throw new Error("Failed to load presets");
      return data;
    },
  });
}

export function useRequestDocuments() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      items: {
        subject: string;
        is_required: boolean;
        note?: string | null;
        is_standard?: boolean;
      }[];
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/documents",
        {
          params: { path: { application_id: input.applicationId } },
          body: {
            items: input.items.map((i) => ({
              subject: i.subject,
              is_required: i.is_required,
              note: i.note ?? null,
              is_standard: i.is_standard ?? false,
            })),
          },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not send the document request"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useReviewDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      requirementId: string;
      accept: boolean;
      rejectReason?: string;
      note?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/documents/{req_id}/review",
        {
          params: {
            path: {
              application_id: input.applicationId,
              req_id: input.requirementId,
            },
          },
          body: {
            accept: input.accept,
            reject_reason: input.rejectReason ?? null,
            note: input.note ?? null,
          },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not save the review"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useDecideApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      approve: boolean;
      reason?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/decision",
        {
          params: { path: { application_id: input.applicationId } },
          body: { approve: input.approve, reason: input.reason ?? null },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not record the decision"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useSelectionReasons() {
  return useQuery({
    queryKey: ["selection-reasons"],
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/api/v1/application-document-presets/selection-reasons",
      );
      if (error || !data) throw new Error("Failed to load selection reasons");
      return data;
    },
  });
}

export function useSelectCandidate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      applicationId: string;
      reasonKey: string;
      reasonText?: string;
    }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/select",
        {
          params: { path: { application_id: input.applicationId } },
          body: { reason_key: input.reasonKey, reason_text: input.reasonText ?? null },
        },
      );
      if (error || !data) throw new Error(apiDetail(error, "Could not select this candidate"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useInviteApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (applicationId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/invite",
        { params: { path: { application_id: applicationId } } },
      );
      if (error || !data)
        throw new Error(apiDetail(error, "Could not invite this candidate"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useRecordAttendance() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { applicationId: string; attended: boolean }) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/attendance",
        {
          params: { path: { application_id: input.applicationId } },
          body: { attended: input.attended },
        },
      );
      if (error || !data)
        throw new Error(apiDetail(error, "Could not register attendance"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}

export function useCreateContractFromApplication() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { applicationId: string; propertyId: string }) => {
      const { data, error } = await apiClient.POST("/api/v1/contracts", {
        body: {
          property_id: input.propertyId,
          application_id: input.applicationId,
          term: "indefinite",
          lease_variant: "roz",
          commission_type: "percentage",
          commission_value: 0,
          commission_vat_rate: 0.21,
          leasing_fee: 0,
          leasing_fee_vat_rate: 0.21,
          bank_guarantee: false,
          rent_from_company: false,
        },
      });
      if (error || !data)
        throw new Error(apiDetail(error, "Could not draft the contract"));
      return data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["applications"] });
      qc.invalidateQueries({ queryKey: ["contracts"] });
    },
  });
}

export function useWithdrawOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (applicationId: string) => {
      const { data, error } = await apiClient.POST(
        "/api/v1/applications/{application_id}/withdraw-offer",
        { params: { path: { application_id: applicationId } } },
      );
      if (error || !data)
        throw new Error(apiDetail(error, "Could not withdraw the offer"));
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["applications"] }),
  });
}
